import React, {FC, useState, useEffect, useRef} from 'react';
import {RouteComponentProps} from '@reach/router';
import {Link} from '@reach/router';
import htmr from 'htmr';
import * as apiService from '../../api';
import Header from '../../Templates/Shared/Header';
import Footer from '../../Templates/Shared/Footer';
import {Lesson} from '../../types/course-registration.type';
import {SvgLoader, SvgProxy} from 'react-svgmt';
import * as styles from '../../styles/certificate/Certificate.module.scss';
import {initializePrices} from '../../Templates/Shared/Utils';
import Helmet from 'react-helmet';
import dayjs from 'dayjs';
import {Container} from 'react-bootstrap';
import SubscribeBanner from '../../Templates/Shared/SubscribeBanner';
import {KeyValuePair} from '../../services/learner/CommonUtils';

interface Props extends RouteComponentProps {
  formId?: string,
  responseId?: string,
  pageData: any,
}

/* eslint-disable camelcase */
interface StudentData {
  assessment_number: string,
  assessment_type: string,
  course_name: string,
  course_regid: string,
  course_slug: string,
  faculty_slug: string,
  locale: string,
  module_name: string,
  module_number: string,
  module_slug: string,
  stage: string,
  student_id: string,
  sub_brand: string,
  user_name: string,
  percentage: number,
  grade: string,
  date: string,
  submitted_at: string,
}

interface ModuleData {
  id: string,
  moduleslug: string,
  certificatedisplayname: string,
  lessons: Lesson[],
}

const ModuleCertificate:FC<Props> = ({
  pageData,
  formId,
  responseId,
}) => {
  const svgRef = useRef<HTMLDivElement>(null);
  const [showError, setError] = useState(false);
  const [leadData, setLeadData] = useState<StudentData>({} as StudentData);
  const [moduleData, setModuleData] = useState<ModuleData | null>(null);
  const pageContent=pageData.brandWebsiteModulecertificate;
  const allCoursesList = [...pageData.englishCourses?.edges,
    ...pageData.nonEnglishCourses?.edges];
  const headerData = pageData.brandWebsiteHeader;
  const footerContent = pageData.brandWebsiteFooter;
  const certficatePageData = pageData.brandWebsitePubliccertificate;
  const siteMetadata = pageData.site.siteMetadata;

  const commonText = certficatePageData?.commontext?.value;
  const commonTextLabels = new KeyValuePair(commonText);

  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yyyy = now.getFullYear();
  const today = dd + '/' + mm + '/' + yyyy;

  const fetchCertificateData = () => {
    new apiService.default.Certificate()
        .getCertificateData(formId, responseId).then((res:any) => {
          const leadData = ({...res.data, date: today});
          if (!leadData['user_name']) {
            throw new Error;
          }
          setLeadData(leadData);
          const course = allCoursesList.filter(
              (course) =>
                course?.node?.courseslug === leadData?.course_slug)?.[0];
          setModuleData(course?.node?.
              modules[parseInt(leadData?.module_number)-1]);
        }).catch(() => {
          setError(true);
        });
  };

  const getOgImageFromGrade = () => {
    if (leadData?.grade === 'Merit') {
      return `${process.env.AssetsCDN}/certificates/merit.png`;
    } else if (leadData?.grade === 'Distinction') {
      return `${process.env.AssetsCDN}/certificates/distinction.png`;
    } else if (leadData?.grade === 'Pass') {
      return `${process.env.AssetsCDN}/certificates/pass.png`;
    }
  };

  useEffect(() => {
    initializePrices();
  }, []);

  useEffect(() => {
    if (formId && responseId) {
      fetchCertificateData();
    }
  }, [formId, responseId]);

  const ogImage = getOgImageFromGrade();
  const title =
    // eslint-disable-next-line max-len
    `I've completed a course in ${leadData?.course_name} from Upskillist with a ${leadData?.grade}`;
  const metaDescription =
    `Upskillist is a certified online learning company providing high quality
    courses globally across a wide range of subjects. I have successfully
    completed my course in ${leadData?.course_name}, check out my certificate.`;
  const ogUrl = window.location.href;

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta charSet="utf-8" />
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={ogUrl} />
        <meta property="og:type" content="website" />
      </Helmet>
      <Header
        headerContent={headerData}
        locale={pageContent.locale}
      />
      <div className={styles.certificateContainer}>
        <main>
          <div className={styles.certificateBanner}>
            <div className={styles.leftSection}>
              <h1>{leadData['user_name']}&#39;s&nbsp;
                {commonTextLabels.getValue('ACHIEVEMENT')}</h1>
              <h2>{leadData['grade']}&nbsp;
                {commonTextLabels.getValue('IN')}&nbsp;
                {leadData.course_name}&nbsp;
                <br />
                {commonTextLabels.getValue('MODULE')}&nbsp;
                {leadData.module_number} </h2>
              <p>{commonTextLabels.getValue('CONGRATULATION_TO')}&nbsp;
                {leadData['user_name']}&nbsp;
                {commonTextLabels.getValue('ON_ACHIEVING_A')}&nbsp;
                {leadData['grade']}&nbsp;
                {commonTextLabels.getValue('SCORE_MODULE')}&nbsp;
                {leadData.module_number}.&nbsp;
                {leadData['user_name']}&#39;s&nbsp;
                {commonTextLabels.getValue('ACHIEVEMENT_PART')}&nbsp;
                {commonTextLabels.getValue('CAREFULLY_DESIGNED')}&nbsp;
                {leadData.course_name}.&nbsp;
                {commonTextLabels.getValue('WHAT_ARE')}  </p>
              <Link to ='/courses/'><button>
                {commonTextLabels.getValue('TRY_FOR_FREE')}</button></Link>
            </div>
            <div className={styles.rightSection}>
              <div className={styles.hideOnMobile}>
                {/* <img src='/images/eCert/certficate-public-2.png' /> */}
                <div className={styles.svgMainWrapper}>
                  <div ref={svgRef} className={styles.svgSection}>
                    <SvgLoader
                      path={`/images/eCert/module-certificate-en-new.svg`}>
                      <SvgProxy
                        selector="#resultPath">
                        {leadData['grade']}
                      </SvgProxy>
                      <SvgProxy
                        selector="#datePath">
                        {dayjs(leadData['submitted_at']).format('DD/MM/YYYY')}
                      </SvgProxy>
                      <SvgProxy
                        selector="#namePath">
                        {leadData['user_name']}
                      </SvgProxy>
                      <SvgProxy
                        selector="#moduleNamePath">
                        {
                    moduleData?.certificatedisplayname ?
                    moduleData?.certificatedisplayname :
                    leadData['module_name']
                        }
                      </SvgProxy>
                    </SvgLoader>
                  </div>
                </div>
              </div>
              <div className={styles.showOnMobile}>
                <div className={styles.svgMainWrapperMobile}>
                  <div ref={svgRef} className={styles.svgSection}>
                    <SvgLoader
                      path={`/images/eCert/module-certificate-en-new.svg`}>
                      <SvgProxy
                        selector="#resultPath">
                        {leadData['grade']}
                      </SvgProxy>
                      <SvgProxy
                        selector="#datePath">
                        {dayjs(leadData['submitted_at']).format('DD/MM/YYYY')}
                      </SvgProxy>
                      <SvgProxy
                        selector="#namePath">
                        {leadData['user_name']}
                      </SvgProxy>
                      <SvgProxy
                        selector="#moduleNamePath">
                        {
                    moduleData?.certificatedisplayname ?
                    moduleData?.certificatedisplayname :
                    leadData['module_name']
                        }
                      </SvgProxy>
                    </SvgLoader>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.partnerBanner}>
            <div className={styles.Partnercontainer}>
              <div className={styles.hideOnMobile}>
                <div className={styles.logoContainer}>
                  <img src='/images/eCert/bolt.png'/>
                  <img src='/images/eCert/adobe.png'/>
                  <img src='/images/eCert/tmobile.png'/>
                  <img src='/images/eCert/vodafone.png'/>
                  <img src='/images/eCert/virgin.png'/>
                </div>
              </div>
              <div className={styles.showOnMobileFlex}>
                <div className={styles.logoContainer}>
                  <img src='/images/eCert/partner-logos.png' />
                </div>
              </div>
            </div>
          </div>
          <div className={styles.partnerReviewSection}>
            <div className={styles.userReviewsOuterContainer}>
              <div className={styles.userReviews}>
                <div className={styles.userName}>
                  <h3 className={styles.userHeading}>
                    {commonTextLabels.getValue('BLAKE_COLLIS')}</h3>
                  <img src='/images/eCert/marcos-paulo-user.png' />
                </div>
                <p><q>&nbsp;{commonTextLabels.getValue('BLAKE_REVIEW')}&nbsp;
                </q></p>
              </div>
            </div>
            <div className={styles.userReviewsOuterContainer}>
              <div className={styles.userReviews}>
                <div className={styles.userName}>
                  <h3 className={styles.userHeading}>
                    {commonTextLabels.getValue('MARILYN_ZILLIOX')}</h3>
                  <img src='/images/eCert/marilyn-user.png' />
                </div>
                <p><q>&nbsp;{commonTextLabels.getValue('MARILYN_REVIEW')}&nbsp;
                </q></p>
              </div>
            </div>
            <div className={styles.userReviewsOuterContainer}>
              <div className={styles.userReviews}>
                <div className={styles.userName}>
                  <h3 className={styles.userHeading}>
                    {commonTextLabels.getValue('BURCKHARD')}</h3>
                  <img src='/images/eCert/buckhard-user.png' />
                </div>
                <p><q>&nbsp;{commonTextLabels.getValue('BURCKHARD_REVIEW')}
                &nbsp;</q></p>
              </div>
            </div>
          </div>

          <div>
            {certficatePageData.sections.map(
                (section:any, sectionIndex:number) => {
                  let content= '<div></div>';
                  if (section.pagesection) {
                    content=section.pagesection.content;
                  }
                  return (<React.Fragment key={`section-${sectionIndex}`}>
                    {htmr(content)}
                  </React.Fragment>);
                })}
          </div>
          <div className={styles.subscribeSection}>
            <Container className={styles.subscribeSectionContainer}>
              <div className={styles.subscriberBannerContainer}>
                <SubscribeBanner />
              </div>
            </Container>
          </div>
        </main>
        <Footer
          footerData={footerContent}
          siteMetadata={siteMetadata}
        />
      </div>
    </>
  );
};

export default ModuleCertificate;
